const LOGIN_CALLBACK_PATH = "/login/callback";

const buildOktaAuthConfig = (oktaDomain, clientId, origin = window.location.origin) => {
  return {
    issuer: `${oktaDomain}/oauth2/default`,
    clientId,
    redirectUri: `${origin}${LOGIN_CALLBACK_PATH}`,
    tokenManager: {
      autoRenew: true,
      // Sync token storage across tabs so one tab's renewal is visible to all tabs.
      syncStorage: true,
    },
    services: {
      // Background renewal service (okta-auth-js 7.x) renews tokens before expiry with
      // cross-tab leader election, preventing multi-tab refresh-token rotation races
      // (the chronic invalid_grant 400s seen in App Insights — MPI-737).
      autoRenew: true,
      autoRemove: true,
      syncStorage: true,
    },
  };
};

const hasAuthorizationHeader = (headers) => {
  if (!headers) {
    return false;
  }

  const authorizationHeader = headers.Authorization || headers.authorization;

  if (!authorizationHeader || typeof authorizationHeader !== "string") {
    return false;
  }

  return authorizationHeader.toLowerCase().startsWith("bearer ");
};

const shouldAttachRenewedToken = (config) => {
  if (!config || !config.url || !hasAuthorizationHeader(config.headers)) {
    return false;
  }

  return config.url.startsWith("api/") || config.url.startsWith("/api/");
};

const isTokenExpired = (
  accessToken,
  currentTimeSeconds = Math.floor(Date.now() / 1000)
) => {
  if (!accessToken) {
    return true;
  }

  try {
    const tokenParts = accessToken.split(".");

    if (tokenParts.length < 2) {
      return true;
    }

    const normalizedPayload = tokenParts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(tokenParts[1].length / 4) * 4, "=");
    const decodedToken = JSON.parse(atob(normalizedPayload));

    if (!decodedToken || typeof decodedToken.exp !== "number") {
      return true;
    }

    return decodedToken.exp <= currentTimeSeconds;
  } catch (error) {
    return true;
  }
};

let inFlightRenewal = null;

const renewWithRetry = async (oktaAuth) => {
  try {
    return await oktaAuth.getOrRenewAccessToken();
  } catch (firstError) {
    // One retry absorbs transient network blips to Okta (frequent per App Insights).
    try {
      return await oktaAuth.getOrRenewAccessToken();
    } catch (secondError) {
      return null;
    }
  }
};

const getOrRenewAccessTokenSingleFlight = (oktaAuth) => {
  // Concurrent API calls share one renewal instead of racing the rotating refresh token.
  if (!inFlightRenewal) {
    inFlightRenewal = renewWithRetry(oktaAuth).finally(() => {
      inFlightRenewal = null;
    });
  }

  return inFlightRenewal;
};

const resetRenewalStateForTests = () => {
  inFlightRenewal = null;
};

const applyRenewedAccessToken = async (config, oktaAuth) => {
  if (!shouldAttachRenewedToken(config)) {
    return config;
  }

  let accessToken = await getOrRenewAccessTokenSingleFlight(oktaAuth);

  if (!accessToken && oktaAuth.getAccessToken) {
    // Renewal failed — keep the session alive with the current token if it is still valid.
    const existingToken = oktaAuth.getAccessToken();

    if (existingToken && !isTokenExpired(existingToken)) {
      accessToken = existingToken;
    }
  }

  if (!accessToken) {
    if (oktaAuth.signInWithRedirect) {
      await oktaAuth.signInWithRedirect();
    }

    throw new Error("Unable to renew Okta access token; redirecting to sign in.");
  }

  const headers = {
    ...config.headers,
    Authorization: `Bearer ${accessToken}`,
  };

  if (headers.authorization) {
    delete headers.authorization;
  }

  return {
    ...config,
    headers,
  };
};

const registerOktaAuthInterceptor = (axiosInstance, oktaAuth) => {
  const interceptorId = axiosInstance.interceptors.request.use((config) =>
    applyRenewedAccessToken(config, oktaAuth)
  );

  return () => {
    axiosInstance.interceptors.request.eject(interceptorId);
  };
};

export {
  LOGIN_CALLBACK_PATH,
  buildOktaAuthConfig,
  shouldAttachRenewedToken,
  applyRenewedAccessToken,
  registerOktaAuthInterceptor,
  isTokenExpired,
  resetRenewalStateForTests,
};